import { type Control, type FieldValues, useFieldArray } from 'react-hook-form';

type FieldArrayProps<TFieldValues extends FieldValues = FieldValues> = {
  name: string;
  control: Control<TFieldValues>;
  defaultValues: Record<string, unknown> | ((index: number) => Record<string, unknown>);
  children: (helpers: {
    fields: { id: string }[];
    handleChange: (event: React.ChangeEvent<HTMLSelectElement>) => void;
  }) => React.ReactNode;
};

function FieldArray<TFieldValues extends FieldValues = FieldValues>({
  name,
  children,
  control,
  defaultValues,
}: FieldArrayProps<TFieldValues>) {
  const { append, remove, fields, ...fieldArray } = useFieldArray({
    control: control as Control<FieldValues>,
    name,
  });

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedNum = parseInt(e.target.value);

    const fieldIndexes = selectedNum - fields.length;

    // removes all
    if (selectedNum === 0) {
      remove();
    }

    // removes item from the array based on the selected num of items
    if (fieldIndexes < 0) {
      for (let i = 0; i < fieldIndexes * -1; i++) {
        remove(fields.length - 1 - i);
      }
      return;
    }

    // adds new items based on the selected option
    if (fieldIndexes > 0) {
      for (let i = 0; i < fieldIndexes; i++) {
        const currentIndex = fields.length + i;
        const values = typeof defaultValues === 'function' ? defaultValues(currentIndex) : defaultValues;
        append(values, { shouldFocus: false });
      }
    }
  };

  return children({ ...fieldArray, fields, handleChange });
}

export default FieldArray;
